/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDatePicker from 'components/MDDatePicker';
import MDDateRange from 'components/MDDateRange';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import stepperName from './ExportStoreStatus';
import ExportStoreList from './ExportStoreList';
import ExportStoreStepper from './ExportStoreStepper';
import ExportStorePrint from './ExportStorePrint';

const initAttr = {
  id: '',
  code: '',
  request_id: '',
  branch_id: '',
  location_id: '',
  copy_id: '',
  branch_receive: '',
  user_id: '',
  type_id: '',
  image_id: '',
  remark: '',
  print: '',
  kind: 'WEB',
  request_code: '',
  status: 'edit',
  created_at: '',
  updated_at: '',
  branch_name: '',
  branch_import: '',
  qrcode: '',
  date_record: Func.dateNow(),
  print_no: '',
};

const initRequest = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  branch_receive: '',
  emp_name: '',
  date_record: '',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 12,
  pageTotal: 0,
  status: 'ALL',
  period: 'ALL',
  branch: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'check', name: stepperName.check.name },
  { id: 'reject', name: stepperName.reject.name },
  { id: 'ready', name: stepperName.ready.name },
  { id: 'liftcar', name: stepperName.liftcar.name },
  { id: 'transport', name: stepperName.transport.name },
  { id: 'receive', name: stepperName.receive.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'cancel', name: stepperName.cancel.name },
];

function ExportStore() {
  const mod = 'export_store';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [request, setRequest] = useState(initRequest);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [stepper, setStepper] = useState({});
  const [tab, setTab] = useState(0);
  const [cboBranch, setCboBranch] = useState([]);
  const [image, setImage] = useState([]);
  const [photo, setPhoto] = useState({});
  const [modalPhoto, setModalPhoto] = useState(false);
  const printRef = useRef();
  const printRef2 = useRef();
  const txtRequest = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      branch: data.branch,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr({ ...result.data, request_code: '' });
        setRequest(result.request);
        setNavigate(result.navigate);
        setStepper(result.stepper);
        setImage(result.image);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const print = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: attr.id,
      lang: app.lang,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setAttr({ ...attr, print: result.data.print, print_no: result.data.print_no });
        setModalPrint(true);
      }
    });
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      request_code: attr.request_code,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`/${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const copy = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/copy`, app.token, {
          id: id,
        }).then((result) => {
          if (result.status === 'ok') {
            window.location.replace(`/${mod}/${result.id}`);
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const remark = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          remark: attr.remark,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: `คุณต้องการเปลี่ยนสถานะเป็น \n"${stepperName[status].name}" ใช่หรือไม่ ?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          remark: attr.remark,
          status: status,
        }).then((result) => {
          if (result.status === 'ok') {
            setLoading(false);
            setAttr(result.data);
            showNotify(t(`alert.editcomplete`), 'success');
            printRef.current.print();
          } else {
            setLoading(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const approvePdf = (status) => {
    Swal.fire({
      title: `คุณต้องการเปลี่ยนสถานะเป็น \n"${stepperName[status].name}" ใช่หรือไม่ ?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          remark: attr.remark,
          status: status,
        }).then((result) => {
          if (result.status === 'ok') {
            setLoading(false);
            setAttr({ ...attr, status: 'check' });
            showNotify(t(`alert.editcomplete`), 'success');
            open();
            print();
          } else {
            setLoading(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 12 })}>
        {t(`element.show_record`, { num: 12 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 24 })}>
        {t(`element.show_record`, { num: 24 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboBranch(result.branch);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap', p: 0 }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>{t('element.show_all')}</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);

                setTimeout(function () {
                  txtRequest.current.focus();
                }, 500);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {record.map((row) => (
                <Grid item xs={12} sm={12} md={6} lg={3} xl={2} key={row.id}>
                  <Card sx={{ overflow: 'hidden' }}>
                    <CardMedia
                      sx={{ height: 160, m: 0, borderRadius: 0 }}
                      image={row.image}
                      title={row.code}
                      onClick={() => {
                        setPhoto(row);
                        setModalPhoto(true);
                      }}
                    />
                    <CardHeader
                      avatar={
                        <Tooltip title={row.status_name}>
                          <Alert
                            icon={<Icon>{row.status_icon}</Icon>}
                            severity={row.status_color}
                            sx={{
                              fontSize: '14px',
                              padding: '0 10px',
                              fontWeight: 400,
                              width: 40,
                              borderRadius: 5,
                            }}
                          />
                        </Tooltip>
                      }
                      title={btnOpen(row)}
                      subheader={row.branch_import}
                      action={<IconButton aria-label='box'>{row.box}</IconButton>}
                    />
                    <CardContent
                      sx={{ px: 2, py: 0, fontSize: 15, color: '#666666', fontWeight: 100 }}
                    >
                      <Grid container>
                        <Grid item xs={6}>
                          ใบขอโอนสินค้า
                        </Grid>
                        <Grid item xs={6} className='text-right color-info'>
                          {row.request_code}
                        </Grid>
                        <Grid item xs={4}>
                          วันที่
                        </Grid>
                        <Grid item xs={8} className='text-right'>
                          {Func.dateTimeDisplay(row.date_approve)}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDBox
                  noValidate
                  component='form'
                  role='form'
                  onSubmit={(e) => {
                    e.preventDefault();
                    add();
                  }}
                >
                  <MDInput
                    label='ใบขอย้าย'
                    name='exportstore'
                    fullWidth
                    value={attr.request_code}
                    variant='standard'
                    type='search'
                    onChange={(e) => setAttr({ ...attr, request_code: e.target.value })}
                    InputProps={{
                      inputRef: txtRequest,
                      inputProps: {
                        maxLength: 20,
                        autoComplete: 'off',
                      },
                    }}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'check' || key === 'cancel';
        case 'receive':
          return key === 'success';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={ExportStoreStepper}>
            {action(key) ? (
              <MDButton
                variant='gradient'
                color={stepperName[key].color}
                size='small'
                onClick={() => approve(key)}
              >
                <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === 'check') return 2;
      if (attr.status === 'reject') return 3;
      if (attr.status === 'ready') return 4;
      if (attr.status === 'liftcar') return 5;
      if (attr.status === 'transport') return 6;
      if (attr.status === 'receive') return 7;
      if (attr.status === 'success') return 8;
      if (attr.status === 'cancel') return 9;

      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('check')}
          {stepLevel('reject')}
          {stepLevel('ready')}
          {stepLevel('liftcar')}
          {stepLevel('transport')}
          {stepLevel('receive')}
          {stepLevel('success')}
          {stepLevel('cancel')}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfoExport = () => {
    const readOnly = attr.status !== 'edit';

    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <Grid container>
          <Grid item xs={12}>
            <MDTypography variant='h5' mb={3}>
              โอนย้าย
            </MDTypography>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='สาขารับเข้า'
              value={attr.branch_import}
              variant='standard'
              multiline
              inputProps={{
                readOnly: readOnly,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='สาขาโอนออก'
              value={attr.branch_name}
              variant='standard'
              multiline
              inputProps={{
                readOnly: readOnly,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDDatePicker
              onChange={([date]) => {
                setAttr({ ...attr, date_record: Func.dateFormat(date) });
              }}
              value={new Date(attr.date_record)}
              input={{
                label: 'วันที่ย้าย',
                variant: 'standard',
                fullWidth: true,
              }}
              readOnly={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              label={t(`attr.remark`)}
              onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
              value={attr.remark}
              variant='standard'
              multiline
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 200,
                  autoComplete: 'off',
                  placeholder: '',
                },
                endAdornment: (
                  <Tooltip title={t(`element.save`)}>
                    <MDButton variant='text' size='small' iconOnly onClick={remark} color='success'>
                      <Icon>save</Icon>
                    </MDButton>
                  </Tooltip>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderInfoRequest = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          ขอโอน
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='รหัสขอโอน'
              value={request.code}
              variant='standard'
              InputProps={{
                endAdornment:
                  attr.status !== 'edit' ? (
                    <InputAdornment position='end'>
                      <MDButton
                        variant='text'
                        tabIndex={-1}
                        color='dark'
                        onClick={() => {
                          setAttr({ ...attr, request_code: request.code });
                          setConfirm(false);
                          setModalAdd(true);

                          setTimeout(function () {
                            txtRequest.current.focus();
                          }, 500);
                        }}
                      >
                        <Icon>post_add</Icon>&nbsp;{t(`element.add`)}
                      </MDButton>
                    </InputAdornment>
                  ) : null,
                autoComplete: 'off',
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='ผู้ขอโอน'
              value={request.emp_name}
              variant='standard'
              multiline
              inputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='วันที่'
              value={Func.dateDisplay(request.date_record)}
              variant='standard'
              multiline
              inputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              label='หมายเหตุ'
              value={request.remark}
              variant='standard'
              multiline
              inputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        {attr.status === 'reject' ? (
          <MDButton variant='gradient' color='warning' onClick={copy}>
            <Icon>content_copy</Icon>&nbsp;{t(`element.copy`)}
          </MDButton>
        ) : null}
        {attr.status === 'edit' ? (
          <Tooltip title='ปุ่มลัด : ['>
            <MDButton variant='gradient' color='success' onClick={() => approve('check')}>
              <Icon>task_outlined</Icon>&nbsp;ส่งตรวจสอบ
            </MDButton>
          </Tooltip>
        ) : null}
        {attr.status !== 'edit' ? (
          <Tooltip title='ปุ่มลัด : ]'>
            <MDButton
              variant='gradient'
              color='dark'
              onClick={() => {
                setAttr({ ...attr, request_code: '' });
                setConfirm(false);
                setModalAdd(true);

                setTimeout(function () {
                  txtRequest.current.focus();
                }, 500);
              }}
            >
              <Icon>post_add</Icon>&nbsp;{t(`element.add`)}
            </MDButton>
          </Tooltip>
        ) : null}
        <ExportStorePrint parent={attr} ref={printRef2} type='2' />
        <ExportStorePrint parent={attr} ref={printRef} type='1' />
      </MDBox>
    );
  };

  const renderApprove = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        {attr.status === 'edit' ? (
          <MDButton variant='gradient' color='info' onClick={() => approve('check')}>
            <Icon>task_outlined</Icon>&nbsp;ส่งตรวจสอบ
          </MDButton>
        ) : null}
      </MDBox>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2}>
        {image.map((item) => (
          <Grid item xs={12} sm={12} md={6} lg={3} key={item.id}>
            <MDBox className='card-img'>
              <CardHeader
                title={`Photo ID : #${item.id}`}
                subheader={Func.dateTimeDisplay(item.created_at)}
              />
              <CardMedia
                sx={{ height: 300, mt: 0 }}
                image={item.image}
                title={item.id}
                onClick={() => {
                  setPhoto(item);
                  setModalPhoto(true);
                }}
              />
              <CardContent>
                <MDTypography variant='body2'>สร้างโดย : {item.user_name}</MDTypography>
              </CardContent>
            </MDBox>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    aria-label='tabs-addedit'
                    sx={{ overflowX: 'scroll' }}
                  >
                    <Tab label='ข้อมูล' />
                    {image.length > 0 ? <Tab label='ภาพประกอบ' /> : null}
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} xl={6}>
                      <MDTypography variant='h3' mb={3}>
                        #{attr.code}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderInfoExport()}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderInfoRequest()}
                    </Grid>
                    <Grid item xs={12}>
                      <MDBox className='card'>
                        <ExportStoreList parent={attr} />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12}>
                      {renderApprove()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  {renderImage()}
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {Func.dateTimeDisplay(photo.created_at)}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xl'
        keepMounted
        aria-describedby='dialog-print'
        fullScreen
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle>
          #{attr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {attr.print === '' ? null : (
            <Iframe
              url={attr.print}
              width='100%'
              height='100%'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const printPdf = () => {
    if (attr.status === 'cancel') return null;

    return (
      <MDButton variant='gradient' color='info' onClick={print}>
        <Icon>print</Icon>&nbsp;{t(`element.print`)}{' '}
        {attr.print_no * 1 > 0 ? `(${attr.print_no})` : ''}
      </MDButton>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useHotkeys('[', () => {
    if (attr.status === 'edit') {
      approve('check');
    }
  });

  useHotkeys(']', () => {
    if (attr.status !== 'edit') {
      setAttr({ ...attr, request_code: '' });
      setConfirm(false);
      setModalAdd(true);

      setTimeout(function () {
        txtRequest.current.focus();
      }, 500);
    }
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.warehouse`), t(`menu.export_store`)]} />
      {renderPage()}
      {renderAdd()}
      {renderPrint()}
      {renderPhoto()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default ExportStore;
